import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  Image,
  Video,
  List,
  ArrowRight,
  TrendingUp,
  FileText,
  GalleryHorizontal,
} from "lucide-react";

const HomePage: React.FC = () => {
  const [stats, setStats] = useState({
    imageTemplates: 0,
    videoTemplates: 0,
    categories: 0,
    carouselItems: 0,
    loading: true,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [imageTemplatesResult, videoTemplatesResult, categoriesResult, carouselResult] =
        await Promise.all([
          supabase
            .from("image_template")
            .select("id", { count: "exact", head: true }),
          supabase
            .from("video_template")
            .select("id", { count: "exact", head: true }),
          supabase
            .from("image_category")
            .select("id", { count: "exact", head: true }),
          supabase
            .from("carousel")
            .select("id", { count: "exact", head: true }),
        ]);

      setStats({
        imageTemplates: imageTemplatesResult.count || 0,
        videoTemplates: videoTemplatesResult.count || 0,
        categories: categoriesResult.count || 0,
        carouselItems: carouselResult.count || 0,
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  const quickLinks = [
    {
      title: "Image Templates",
      description: "Manage and create image templates",
      path: "/image/templates",
      icon: <Image className="w-8 h-8" />,
      color: "bg-blue-500",
      hoverColor: "hover:bg-blue-600",
    },
    {
      title: "Video Templates",
      description: "Manage and create video templates",
      path: "/video/templates",
      icon: <Video className="w-8 h-8" />,
      color: "bg-purple-500",
      hoverColor: "hover:bg-purple-600",
    },
    {
      title: "Categories",
      description: "Organize your template categories",
      path: "/categories",
      icon: <List className="w-8 h-8" />,
      color: "bg-green-500",
      hoverColor: "hover:bg-green-600",
    },
    {
      title: "Carousel",
      description: "Manage carousel banners and slides",
      path: "/carousel",
      icon: <GalleryHorizontal className="w-8 h-8" />,
      color: "bg-orange-500",
      hoverColor: "hover:bg-orange-600",
    },
  ];

  return (
    <div className="space-y-8">
      <div className="">
        <h1 className="text-4xl font-bold mb-2">Welcome to Berrys Admin</h1>
        <p className="text-lg">
          Manage your templates and categories from one central dashboard
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium mb-1">
                Image Templates
              </p>
              <p className="text-3xl font-bold text-gray-800">
                {stats.loading ? (
                  <span className="animate-pulse">...</span>
                ) : (
                  stats.imageTemplates
                )}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Image className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>Total templates</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium mb-1">
                Video Templates
              </p>
              <p className="text-3xl font-bold text-gray-800">
                {stats.loading ? (
                  <span className="animate-pulse">...</span>
                ) : (
                  stats.videoTemplates
                )}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Video className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>Total templates</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium mb-1">
                Categories
              </p>
              <p className="text-3xl font-bold text-gray-800">
                {stats.loading ? (
                  <span className="animate-pulse">...</span>
                ) : (
                  stats.categories
                )}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <List className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <FileText className="w-4 h-4 mr-1" />
            <span>Active categories</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium mb-1">
                Carousel Items
              </p>
              <p className="text-3xl font-bold text-gray-800">
                {stats.loading ? (
                  <span className="animate-pulse">...</span>
                ) : (
                  stats.carouselItems
                )}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <GalleryHorizontal className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>Total slides</span>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {quickLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-200 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`${link.color} ${link.hoverColor} p-3 rounded-lg text-white transition-colors`}
                >
                  {link.icon}
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-berrys-orange group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {link.title}
              </h3>
              <p className="text-gray-600 text-sm">{link.description}</p>
            </Link>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Getting Started
        </h2>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="bg-berrys-orange text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
              1
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">
                Create Categories
              </h3>
              <p className="text-gray-600 text-sm">
                Organize your templates by creating categories first. This helps
                you manage and filter templates more efficiently.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-berrys-orange text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
              2
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">
                Add Templates
              </h3>
              <p className="text-gray-600 text-sm">
                Create image or video templates with AI models, prompts, and
                outputs. Upload multiple files and organize them by category.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-berrys-orange text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
              3
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">
                Manage & Edit
              </h3>
              <p className="text-gray-600 text-sm">
                Edit existing templates, update categories, and keep your
                content organized. All changes are saved automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
